import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { TOUR_CONFIG } from "tour/tourConfig";
import { TourKey } from "tour/tourKeys";

type TourState = {
  currentStep: number;
  stepsCount: number;
  isRunning: boolean;
  isFinished: boolean;
  isDBfinished: boolean;
};

export type ToursState = Record<TourKey, TourState> & {
  hydrated: boolean;
};

export type BackendTourPayload = {
  tour_type: "web" | "app";
  user_guide: BackendUserGuide;
};

type BackendUserGuide =
  | [] // when backend sends empty
  | Partial<Record<TourKey, { isFinished: boolean }>>;

// backend
export type FinishTourPayload = {
  tour_type: "web" | "app";
  user_guide: {
    [K in TourKey]?: {
      isFinished: boolean;
    };
  };
};

const initialTour = (key: TourKey): TourState => ({
  currentStep: 1,
  stepsCount: TOUR_CONFIG[key].length,
  isRunning: false,
  isFinished: false,
  isDBfinished: false,
});

/**
 * =========================
 * INITIAL REDUX STATE
 * =========================
 */
const initialState: ToursState = {
  hydrated: false,
  dashboard: initialTour("dashboard"),
  quote: initialTour("quote"),
  quoteSummary: initialTour("quoteSummary"),
  quoteItems: initialTour("quoteItems"),
  quoteSections: initialTour("quoteSections"),
  quoteDetails: initialTour("quoteDetails"),
  presetQuote: initialTour("presetQuote"),
  invoice: initialTour("invoice"),
  invoiceSummary: initialTour("invoiceSummary"),
  invoiceItems: initialTour("invoiceItems"),
  settings: initialTour("settings"),
};

/**
 * =========================
 * SLICE
 * =========================
 */
const tourSlice = createSlice({
  name: "tours",
  initialState,
  reducers: {
    startTour(state, action: PayloadAction<TourKey>) {
      const tour = state[action.payload];
      tour.isRunning = true;
      tour.isFinished = false;
      tour.currentStep = 1;
    },

    nextStep(state, action: PayloadAction<TourKey>) {
      const key = action.payload;
      const tour = state[key];

      if (tour.currentStep < tour.stepsCount) tour.currentStep++;
    },

    prevStep(state, action: PayloadAction<TourKey>) {
      const tour = state[action.payload];
      if (tour.currentStep > 1) tour.currentStep--;
    },

    finishTour(state, action: PayloadAction<TourKey>) {
      const key = action.payload;
      const tour = state[key];

      tour.isRunning = false;
      tour.isFinished = true;
      tour.isDBfinished = true;
    },

    restartTour(state, action: PayloadAction<TourKey>) {
      const key = action.payload;

      const isDBfinished = state[key].isDBfinished;

      state[key] = {
        ...initialTour(key),
        isDBfinished,
      };
    },
    restartAllTours() {
      return initialState;
    },

    hydrateToursFromBackend(
      state,
      action: PayloadAction<{
        tour_type: "web" | "app";
        user_guide:
          | []
          | Partial<Record<TourKey, boolean | { isFinished: boolean }>>;
      }>
    ) {
      state.hydrated = true;

      const { user_guide } = action.payload;

      if (Array.isArray(user_guide)) return;

      (Object.keys(state) as (TourKey | "hydrated")[]).forEach((key) => {
        if (key === "hydrated") return;

        const backendValue = user_guide[key as TourKey];
        if (backendValue === undefined) return;

        const isFinished =
          typeof backendValue === "boolean"
            ? backendValue
            : backendValue.isFinished;

        if (isFinished) {
          state[key as TourKey].isDBfinished = true;
          state[key as TourKey].isFinished = true;
          state[key as TourKey].isRunning = false;
          state[key as TourKey].currentStep = state[key as TourKey].stepsCount;
        } else {
          state[key as TourKey].isFinished = false;
          state[key as TourKey].currentStep = 1;
        }
      });
    },
  },
});

export const {
  startTour,
  nextStep,
  prevStep,
  finishTour,
  restartTour,
  restartAllTours,
  hydrateToursFromBackend,
} = tourSlice.actions;

export default tourSlice.reducer;
